import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import BookingService from 'services/booking';
import BookingSlotService from 'services/bookingSlot';
import ShopDayOffService from 'services/shopDayOff';
import dayjs, { toUTCString } from 'utils/dayjs';
import { Dayjs } from 'dayjs';
import { get } from 'utils/lodash';
import { Booking, BookingSlot, ShopDayOff } from './types';

interface BookingState {
  bookings: Booking[];
  selectedBooking: Booking | null;
  selectedDate: Dayjs;
  bookingSlots: BookingSlot[];
  shopDayOffs: ShopDayOff[];
  slotUpdateLoading: Record<string, boolean>;
  isLoading: boolean;
  isLoadingSlots: boolean;
  isLoadingSlotsData: boolean;
  isLoadingDayOffs: boolean;
  error: string | null;
  formValues?: any;

  // Actions
  loadBookings: (date: Dayjs, shopId: string) => Promise<void>;
  loadBookingSlots: (day?: any, shopId?: string) => Promise<any>;
  loadShopDayOffs: (day?: any, shopId?: string) => Promise<void>;
  onChangeDay: (day: any, shopId?: string) => Promise<void>;
  setSelectedDate: (date: Dayjs) => void;
  setSelectedBooking: (booking: Booking | null) => void;
  setSlotUpdateLoading: (slotId: string, loading: boolean) => void;
  updateSlotOffOptimistic: (slotId: string, slotOffId: string | null, value: boolean) => void;
  updateSlotOffInCache: (slotId: string, slotOffData: any) => void;
  acceptBooking: (id: string) => Promise<void>;
  cancelBooking: (id: string, reason?: string) => Promise<void>;
  completeBooking: (id: string) => Promise<void>;
  updateBookingStatus: (id: string, status: Booking['status']) => Promise<void>;
  setFormValues: (values: any) => void;
  clearError: () => void;
}

const buildQuery = (date: Dayjs, shopId: string) => {
  const fromDate = toUTCString(date.subtract(2, 'day'));
  const toDate = toUTCString(date.add(15, 'day'));

  return {
    where: {
      AND: [
        {
          bookingDate: {
            greater_than: fromDate,
          },
        },
        {
          bookingDate: {
            less_than: toDate,
          },
        },
        shopId && {
          shop: {
            equals: shopId,
          },
        },
      ].filter(Boolean),
    },
    page: 1,
    limit: 200,
  };
};

export const useBookingStore = create<BookingState>()(
  devtools(
    (set, get) => ({
      bookings: [],
      selectedBooking: null,
      selectedDate: dayjs().startOf('day'),
      bookingSlots: [],
      shopDayOffs: [],
      slotUpdateLoading: {},
      isLoading: false,
      isLoadingSlots: false,
      isLoadingSlotsData: false,
      isLoadingDayOffs: false,
      error: null,
      formValues: { date: dayjs() },

      loadBookings: async (date, shopId) => {
        set({ isLoading: true, error: null });
        try {
          const query = buildQuery(date, shopId);
          const response = await BookingService.find({
            ...query,
            fetchPolicy: 'cache-first', // Use cache-first for better performance
            errorPolicy: 'all', // Allow partial data even if some fields are null
          });

          // Ensure response.data is an array before filtering
          const rawBookings = response?.data;
          const bookingsArray = Array.isArray(rawBookings) ? rawBookings : [];

          // Filter out bookings with critical missing data but keep those with null user
          const bookings = bookingsArray.filter(
            (booking: any) => booking?.id && booking?.bookingDate
          ) as Booking[];

          set({
            bookings,
            selectedDate: date,
            isLoading: false,
          });

          // Log any GraphQL errors for monitoring
          if (response?.errors) {
            console.warn('GraphQL errors in bookings (partial data returned):', response.errors);
          }
        } catch (error: any) {
          // Even with errorPolicy: 'all', we might get network errors
          set({
            error: error.message || 'Failed to load bookings',
            isLoading: false,
            bookings: [], // Clear bookings on complete failure
          });
          console.error('Error loading bookings:', error);
        }
      },

      loadBookingSlots: async (day, shopId) => {
        const state = get();
        const date = day || state.formValues?.date || state.selectedDate;
        
        // Map day number to enum values expected by GraphQL
        const dayMapping: { [key: number]: string } = {
          0: 'sun',
          1: 'mon',
          2: 'tue',
          3: 'wed',
          4: 'thu',
          5: 'fri',
          6: 'sat'
        };
        
        const dayOfWeek = date ? dayMapping[dayjs(date).day()] : '';

        if (!dayOfWeek || !shopId) return [];

        set({ isLoadingSlotsData: true, isLoadingSlots: true });
        try {
          const response = await BookingSlotService.loadBookingSlots({
            page: 1,
            limit: 100,
            where: {
              shop: { equals: shopId },
              ...(!!dayOfWeek && {
                dayOfWeek: { equals: dayOfWeek },
              }),
            },
            date: date ? dayjs(date).startOf('day') : '',
            fetchPolicy: 'cache-first',
          });

          const data = response?.data || [];
          set({
            bookingSlots: data,
            isLoadingSlotsData: false,
            isLoadingSlots: false,
          });
          return data;
        } catch (error: any) {
          console.error('Error loading booking slots:', error);
          set({
            bookingSlots: [],
            error: error.message || 'Failed to load booking slots',
            isLoadingSlotsData: false,
            isLoadingSlots: false,
          });
          return [];
        }
      },

      loadShopDayOffs: async (day, shopId) => {
        const state = get();
        const date = day || state.formValues?.date || state.selectedDate;

        if (!shopId) return;

        set({ isLoadingDayOffs: true });
        try {
          const response = await ShopDayOffService.find({
            page: 1,
            limit: 100,
            where: {
              shop: { equals: shopId },
              date: {
                equals: toUTCString(dayjs(date).startOf('day')),
              },
            },
            fetchPolicy: 'cache-first',
          });

          const data = response?.data || [];
          set({
            shopDayOffs: data,
            isLoadingDayOffs: false,
          });
        } catch (error: any) {
          console.error('Error loading shop day offs:', error);
          set({
            shopDayOffs: [],
            error: error.message || 'Failed to load shop day offs',
            isLoadingDayOffs: false,
          });
        }
      },

      onChangeDay: async (day, shopId) => {
        const state = get();
        set({ formValues: { ...state.formValues, date: day } });

        if (shopId) {
          await Promise.all([
            state.loadShopDayOffs(day, shopId),
            state.loadBookingSlots(day, shopId),
          ]);
        }
      },

      setSelectedDate: (date) => {
        set({ selectedDate: date });
      },

      setSelectedBooking: (booking) => {
        set({ selectedBooking: booking });
      },

      setSlotUpdateLoading: (slotId, loading) => {
        set((state) => ({
          slotUpdateLoading: {
            ...state.slotUpdateLoading,
            [slotId]: loading,
          },
        }));
      },

      updateSlotOffOptimistic: (slotId, slotOffId, value) => {
        set((state) => ({
          bookingSlots: state.bookingSlots.map((slot) => {
            if (slot.id === slotId) {
              if (slotOffId) {
                // Update existing slot off
                return {
                  ...slot,
                  bookingSlotOff: {
                    docs: slot.bookingSlotOff?.docs?.map((doc: any) =>
                      doc.id === slotOffId ? { ...doc, value } : doc
                    ) || [{ id: slotOffId, value, date: state.selectedDate.toISOString() }],
                    hasNextPage: slot.bookingSlotOff?.hasNextPage || false,
                  },
                };
              } else {
                // Create new slot off
                const tempSlotOff = {
                  id: `temp-${Date.now()}`,
                  slot: slotId,
                  date: state.selectedDate.toISOString(),
                  value,
                };
                return {
                  ...slot,
                  bookingSlotOff: {
                    docs: [tempSlotOff],
                    hasNextPage: false,
                  },
                };
              }
            }
            return slot;
          }),
        }));
      },

      updateSlotOffInCache: (slotId: string, slotOffData: any) => {
        set((state) => ({
          bookingSlots: state.bookingSlots.map((slot) => {
            if (slot.id === slotId) {
              return {
                ...slot,
                bookingSlotOff: {
                  docs: slotOffData ? [slotOffData] : [],
                  hasNextPage: false,
                },
              };
            }
            return slot;
          }),
        }));
      },

      acceptBooking: async (id) => {
        set({ isLoading: true, error: null });
        try {
          await BookingService.acceptedBooking({
            id,
          });

          // Update local state
          set((state) => ({
            bookings: state.bookings.map((booking) =>
              booking.id === id
                ? { ...booking, status: 'accepted' }
                : booking
            ),
            isLoading: false,
          }));
        } catch (error: any) {
          set({
            error: error.message || 'Failed to accept booking',
            isLoading: false,
          });
          throw error;
        }
      },

      cancelBooking: async (id, reason) => {
        set({ isLoading: true, error: null });
        try {
          await BookingService.cancelBooking({
            id,
            cancelReason: reason,
          });

          // Update local state
          set((state) => ({
            bookings: state.bookings.map((booking) =>
              booking.id === id
                ? { ...booking, status: 'cancelled' as Booking['status'] }
                : booking
            ),
            isLoading: false,
          }));
        } catch (error: any) {
          set({
            error: error.message || 'Failed to cancel booking',
            isLoading: false,
          });
          throw error;
        }
      },

      completeBooking: async (id) => {
        set({ isLoading: true, error: null });
        try {
          await BookingService.doneBooking({
            id,
          });

          // Update local state
          set((state) => ({
            bookings: state.bookings.map((booking) =>
              booking.id === id ? { ...booking, status: 'done' } : booking
            ),
            isLoading: false,
          }));
        } catch (error: any) {
          set({
            error: error.message || 'Failed to complete booking',
            isLoading: false,
          });
          throw error;
        }
      },

      updateBookingStatus: async (id, status) => {
        set({ isLoading: true, error: null });
        try {
          // Use specific methods based on status
          if (status === 'accepted') {
            await BookingService.acceptedBooking({ id });
          } else if (status === 'done') {
            await BookingService.doneBooking({ id });
          } else if (status === 'cancelled') {
            await BookingService.cancelBooking({ id });
          } else {
            throw new Error(`Unsupported status: ${status}`);
          }

          // Update local state
          set((state) => ({
            bookings: state.bookings.map((booking) =>
              booking.id === id ? { ...booking, status } : booking
            ),
            isLoading: false,
          }));
        } catch (error: any) {
          set({
            error: error.message || 'Failed to update booking status',
            isLoading: false,
          });
          throw error;
        }
      },

      setFormValues: (values) => {
        set({ formValues: values });
      },

      clearError: () => {
        set({ error: null });
      },
    }),
    { name: 'booking-store' }
  )
);